import { httpClient } from '@/core/api/http-client'
import type {
  Modifier,
  ModifierDetail,
  ModifierInput,
  ModifierKind,
} from '@/domain/entities/modifier.entity'
import type { Paged, PageParams } from '@/domain/entities/paging'
import type { ModifierRepository } from '@/domain/repositories/modifier.repository'
import { unwrap, unwrapList, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { ModifierDetailDto, ModifierDto, ModifierProductsDto } from './modifier.dto'
import { modifierMapper } from './modifier.mapper'

/**
 * Satu kelas melayani /variants dan /addons: kontrak keduanya identik, `kind`
 * langsung menjadi segmen path.
 */
export class ModifierHttpRepository implements ModifierRepository {
  async list(kind: ModifierKind, outletId: string, params: PageParams): Promise<Paged<Modifier>> {
    const raw = await httpClient.get<unknown>(`/${kind}`, {
      params: {
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
      },
    })
    const paged = unwrapPaged<ModifierDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(modifierMapper.toModifier) }
  }

  async listAll(kind: ModifierKind, outletId: string): Promise<Modifier[]> {
    const raw = await httpClient.get<unknown>(`/${kind}`, {
      params: { outlet_id: outletId, active: true, limit: 100 },
    })
    return unwrapList<ModifierDto>(raw).map(modifierMapper.toModifier)
  }

  async get(kind: ModifierKind, id: string): Promise<ModifierDetail> {
    const raw = await httpClient.get<unknown>(`/${kind}/${id}`)
    return modifierMapper.toModifierDetail(unwrap<ModifierDetailDto>(raw))
  }

  async setProducts(kind: ModifierKind, id: string, productIds: string[]): Promise<string[]> {
    // BE menolak (400) kalau field product_ids tidak dikirim — array kosong
    // adalah cara sah untuk melepas semua tautan, jadi selalu kirim.
    const raw = await httpClient.put<unknown>(`/${kind}/${id}/products`, {
      product_ids: productIds,
    })
    return unwrap<ModifierProductsDto>(raw).product_ids ?? []
  }

  async create(kind: ModifierKind, outletId: string, input: ModifierInput): Promise<Modifier> {
    const raw = await httpClient.post<unknown>(`/${kind}`, {
      outlet_id: outletId,
      name: input.name,
      // SKU nullable di BE; kirim string kosong berarti "tanpa SKU".
      sku: input.sku ?? '',
      source_id: input.sourceId || null,
      price1: input.price1,
      price2: input.price2,
      price3: input.price3,
      manual_hpp: input.manualHpp ?? null,
      // Hanya dikirim untuk modifier privat — mengirim string kosong pada
      // create global tidak perlu, dan absennya field berarti "global".
      ...(input.ownerProductId ? { owner_product_id: input.ownerProductId } : {}),
    })
    return modifierMapper.toModifier(unwrap<ModifierDto>(raw))
  }

  async update(kind: ModifierKind, id: string, input: ModifierInput): Promise<Modifier> {
    const raw = await httpClient.put<unknown>(`/${kind}/${id}`, {
      name: input.name,
      sku: input.sku ?? '',
      source_id: input.sourceId || null,
      price1: input.price1,
      price2: input.price2,
      price3: input.price3,
      manual_hpp: input.manualHpp ?? null,
    })
    return modifierMapper.toModifier(unwrap<ModifierDto>(raw))
  }

  async remove(kind: ModifierKind, id: string): Promise<void> {
    await httpClient.delete(`/${kind}/${id}`)
  }

  async setActive(kind: ModifierKind, id: string, active: boolean): Promise<void> {
    await httpClient.put(`/${kind}/${id}`, { active })
  }
}
